'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import toast from 'react-hot-toast';
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import {
  WizardCard,
  SectionHeader,
  inputClass,
} from '@/components/event-wizard/wizard-ui';
import {
  getApiErrorMessage,
  organizerPaymentProfileApi,
  organizerSettlementReadinessApi,
} from '@/lib/api';

export type PayoutVerificationState = {
  paymentProfileReady: boolean;
  kycApproved: boolean;
  settlementVerified: boolean;
  ready: boolean;
  platformGatewayConnected?: boolean;
};

type Props = {
  eventId?: string | null;
  returnUrl?: string;
  fieldErrors?: {
    payment_verification?: string;
    kyc_verification?: string;
    settlement_verification?: string;
  };
  onVerificationChange?: (state: PayoutVerificationState) => void;
  /** Match event workspace admin styling when embedded in dashboard pages. */
  variant?: 'wizard' | 'admin';
  /** Hide card title when the page hero already shows it. */
  hideTitle?: boolean;
};

type Readiness = {
  platform_gateway?: {
    connected?: boolean;
    label?: string;
    description?: string;
    mode?: string | null;
  };
  razorpay_onboarding?: {
    complete?: boolean;
    label?: string;
    description?: string;
    status?: string;
    resolution_url?: string | null;
  };
  settlement_account?: {
    verified?: boolean;
    label?: string;
    description?: string;
    masked_account?: string | null;
    bank_name?: string | null;
  };
  event_settlement_account?: {
    verified?: boolean;
    label?: string;
    masked_account?: string | null;
    bank_name?: string | null;
  } | null;
  event_verification?: {
    approved?: boolean;
    label?: string;
    description?: string;
    required?: boolean;
  } | null;
  payout_readiness?: {
    status?: string;
    label?: string;
    reasons?: string[];
  };
  onboarding_link_is_stub?: boolean;
};

export function PaymentGatewayVerificationCard({
  eventId,
  returnUrl,
  fieldErrors = {},
  onVerificationChange,
  variant = 'wizard',
  hideTitle = false,
}: Props) {
  void variant;
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [showOnboardForm, setShowOnboardForm] = useState(false);
  const [form, setForm] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    gateway: 'razorpay',
  });
  const onVerificationChangeRef = useRef(onVerificationChange);

  useEffect(() => {
    onVerificationChangeRef.current = onVerificationChange;
  }, [onVerificationChange]);

  const publishState = (next: Readiness | null) => {
    const paymentProfileReady = Boolean(next?.razorpay_onboarding?.complete);
    const kycApproved = Boolean(next?.event_verification?.approved);
    const settlementVerified =
      Boolean(next?.settlement_account?.verified) ||
      Boolean(next?.event_settlement_account?.verified);
    const platformGatewayConnected = Boolean(next?.platform_gateway?.connected);
    onVerificationChangeRef.current?.({
      paymentProfileReady,
      kycApproved,
      settlementVerified,
      platformGatewayConnected,
      ready: Boolean(next?.payout_readiness?.status === 'ready'),
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await organizerSettlementReadinessApi.show(eventId || undefined);
      const data = res.data?.data ?? null;
      setReadiness(data);
      publishState(data);
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 403 && status !== 404) {
        toast.error(getApiErrorMessage(error, 'Unable to load settlement readiness.'));
      }
      setReadiness(null);
      publishState(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  const startOnboarding = async () => {
    if (!form.business_name.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error('Business name, contact name, and email are required.');
      return;
    }

    setCreating(true);
    try {
      await organizerPaymentProfileApi.create(form);
      toast.success('Razorpay onboarding started. Complete verification next.');
      setShowOnboardForm(false);
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to start Razorpay onboarding.'));
    } finally {
      setCreating(false);
    }
  };

  const syncProfile = async () => {
    setSyncing(true);
    try {
      await organizerPaymentProfileApi.sync();
      toast.success('Onboarding status refreshed.');
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to refresh onboarding status.'));
    } finally {
      setSyncing(false);
    }
  };

  const continueOnboarding = async () => {
    setSyncing(true);
    try {
      await organizerPaymentProfileApi.continueOnboarding({
        contact_name: form.contact_name || undefined,
        email: form.email || undefined,
      });
      toast.success('Razorpay onboarding step submitted. Status will refresh from Razorpay.');
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to continue Razorpay onboarding.'));
    } finally {
      setSyncing(false);
    }
  };

  const platformReady = Boolean(readiness?.platform_gateway?.connected);
  const onboardingReady = Boolean(readiness?.razorpay_onboarding?.complete);
  const settlementReady =
    Boolean(readiness?.settlement_account?.verified) ||
    Boolean(readiness?.event_settlement_account?.verified);
  const settlementMasked =
    readiness?.settlement_account?.masked_account ||
    readiness?.event_settlement_account?.masked_account ||
    null;
  const settlementBank =
    readiness?.settlement_account?.bank_name ||
    readiness?.event_settlement_account?.bank_name ||
    null;
  const kycReady = Boolean(readiness?.event_verification?.approved);
  const overallReady = readiness?.payout_readiness?.status === 'ready';

  return (
    <WizardCard>
      {!hideTitle ? (
        <SectionHeader
          title="Payment & settlement readiness"
          description="Platform gateway is managed by EnthiosHub. Complete organizer onboarding and settlement details for payouts."
        />
      ) : null}

      {loading ? (
        <div className="mt-5 flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking settlement readiness…
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VerificationStep
            title="Payment Gateway"
            description={
              readiness?.platform_gateway?.description ||
              "Payments are processed through EnthiosHub's payment gateway."
            }
            complete={platformReady}
            statusLabel={
              platformReady
                ? `Connected${readiness?.platform_gateway?.mode ? ` · ${String(readiness.platform_gateway.mode).toUpperCase()}` : ''}`
                : 'Unavailable'
            }
          >
            <p className="text-xs text-slate-500">Platform-managed · Razorpay</p>
          </VerificationStep>

          <VerificationStep
            title="Organizer Settlement Account"
            description={
              readiness?.settlement_account?.description ||
              'Add a bank account to receive eligible settlements.'
            }
            complete={settlementReady}
            error={fieldErrors.settlement_verification}
            statusLabel={readiness?.settlement_account?.label || 'Not verified'}
          >
            {settlementMasked ? (
              <p className="mb-2 text-sm text-slate-700">
                {settlementBank || 'Bank'} · {settlementMasked}
              </p>
            ) : null}
            <Link
              href="/dashboard/organizer/settlement-account"
              className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-2 text-sm text-slate-700"
            >
              <ShieldCheck className="h-4 w-4" />
              {settlementReady ? 'View settlement account' : 'Verify settlement account'}
            </Link>
          </VerificationStep>

          <VerificationStep
            title="Razorpay Organizer Onboarding"
            description={
              readiness?.razorpay_onboarding?.description ||
              'Linked Account verification is required before settlements can be released.'
            }
            complete={onboardingReady}
            error={fieldErrors.payment_verification}
            statusLabel={readiness?.razorpay_onboarding?.label || 'Required'}
          >
            {!onboardingReady && !showOnboardForm ? (
              <button
                type="button"
                onClick={() => setShowOnboardForm(true)}
                className="rounded-lg bg-[#697C70] px-4 py-2 text-sm font-medium text-text-inverse"
              >
                Start Razorpay onboarding
              </button>
            ) : null}

            {showOnboardForm ? (
              <div className="space-y-3 rounded-xl border border-neutral-100 bg-neutral-50/70 p-4">
                <p className="text-xs text-slate-500">
                  This creates a Razorpay Linked Account for settlements — not EnthiosHub platform API keys.
                </p>
                <input
                  className={inputClass}
                  placeholder="Business name"
                  value={form.business_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, business_name: e.target.value }))}
                />
                <input
                  className={inputClass}
                  placeholder="Contact name"
                  value={form.contact_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, contact_name: e.target.value }))}
                />
                <input
                  className={inputClass}
                  type="email"
                  placeholder="Business email"
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={creating}
                    onClick={() => void startOnboarding()}
                    className="rounded-lg bg-[#697C70] px-4 py-2 text-sm font-medium text-text-inverse disabled:opacity-60"
                  >
                    {creating ? 'Starting…' : 'Continue'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowOnboardForm(false)}
                    className="rounded-lg px-4 py-2 text-sm text-slate-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}

            {readiness?.razorpay_onboarding ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {!onboardingReady ? (
                  <button
                    type="button"
                    disabled={syncing}
                    onClick={() => void continueOnboarding()}
                    className="inline-flex items-center gap-2 rounded-lg bg-[#697C70] px-4 py-2 text-sm font-medium text-text-inverse disabled:opacity-60"
                  >
                    {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    Complete Razorpay onboarding
                  </button>
                ) : null}
                {readiness.razorpay_onboarding.resolution_url ? (
                  <a
                    href={readiness.razorpay_onboarding.resolution_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg bg-[#697C70] px-4 py-2 text-sm font-medium text-text-inverse"
                  >
                    Complete verification on Razorpay
                  </a>
                ) : null}
                <button
                  type="button"
                  disabled={syncing}
                  onClick={() => void syncProfile()}
                  className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-2 text-sm text-slate-700"
                >
                  {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Refresh Razorpay status
                </button>
                <Link
                  href="/dashboard/organizer/settlement-account"
                  className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-2 text-sm text-slate-700"
                >
                  Manage onboarding
                </Link>
              </div>
            ) : null}
          </VerificationStep>

          {readiness?.event_verification ? (
            <VerificationStep
              title="Event-specific verification"
              description={
                readiness.event_verification.description ||
                'Event-specific verification only. It does not activate Razorpay Route settlements.'
              }
              complete={kycReady}
              error={fieldErrors.kyc_verification}
              statusLabel={readiness.event_verification.label || 'Required'}
            >
              <Link
                href={eventId ? `/dashboard/organizer/kyc?event=${eventId}` : '/dashboard/organizer/kyc'}
                className="inline-flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-2 text-sm text-slate-700"
              >
                <ShieldCheck className="h-4 w-4" />
                {kycReady ? 'View verification' : 'Complete event verification'}
              </Link>
            </VerificationStep>
          ) : null}

          <VerificationStep
            title="Payout readiness"
            description="Settlement eligibility for this event."
            complete={overallReady}
            statusLabel={readiness?.payout_readiness?.label || 'Not ready'}
          >
            {(readiness?.payout_readiness?.reasons || []).length > 0 ? (
              <ul className="list-disc space-y-1 pl-4 text-xs text-slate-600">
                {(readiness?.payout_readiness?.reasons || []).map((reason, idx) => (
                  <li key={`${reason}-${idx}`}>{reason}</li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-emerald-800">Settlement eligibility checks passed.</p>
            )}
          </VerificationStep>

          {!overallReady ? (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Complete settlement account, Razorpay onboarding, and any event-specific verification before
                publishing paid tickets. You do not connect EnthiosHub&apos;s payment gateway.
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              <CheckCircle2 className="h-4 w-4" />
              Settlement readiness is complete for this event.
            </div>
          )}
        </div>
      )}
    </WizardCard>
  );
}

function VerificationStep({
  title,
  description,
  complete,
  statusLabel,
  error,
  children,
}: {
  title: string;
  description: string;
  complete: boolean;
  statusLabel: string;
  error?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-neutral-100 bg-neutral-50/60 p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            {complete ? (
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-amber-600" />
            )}
            <h3 className="text-sm font-semibold text-dark">{title}</h3>
          </div>
          <p className="mt-1 text-xs text-slate-500">{description}</p>
        </div>
        <span
          className={`rounded-full border px-2.5 py-1 text-xs font-medium ${
            complete ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          {statusLabel}
        </span>
      </div>
      {children ? <div className="mt-3">{children}</div> : null}
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}
